/**
 * When an invite stops being usable. `createInvite` stamps `expiresAt`, the
 * ISO instant seven days after creation, and that date is the only authority
 * on expiry: nothing writes an "expired" status. The link path, the tokenless
 * sign-in path, the duplicate-invite check and the Users page all compare the
 * date to now, so an aged-out invite reads the same everywhere without anyone
 * having to touch the document. A value that is not a readable ISO string
 * counts as expired — these are auth gates, and an unreadable date must close
 * them, not hold them open.
 */

/** How long an invite can be used. */
export const INVITE_TTL_MS = 7 * 24 * 60 * 60 * 1000

/** The instant the invite expires, or null when the stored value cannot be read. */
export function inviteExpiresAtMs(expiresAt: unknown): number | null {
  if (typeof expiresAt !== 'string' || !expiresAt) return null
  const ms = Date.parse(expiresAt)
  return Number.isNaN(ms) ? null : ms
}

export function isInviteExpired(expiresAt: unknown, nowMs: number = Date.now()): boolean {
  const ms = inviteExpiresAtMs(expiresAt)
  return ms === null || ms <= nowMs
}

/** Usable right now: untouched (`status: 'pending'`) and not past its date. */
export function isInviteLive(
  invite: { status?: unknown; expiresAt?: unknown },
  nowMs: number = Date.now(),
): boolean {
  return invite.status === 'pending' && !isInviteExpired(invite.expiresAt, nowMs)
}
